import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  feedFormulaService,
  rawMaterialService,
} from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { FeedFormula, FeedFormulaItem, RawMaterial } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

interface FormItem {
  raw_material_id: string;
  percentage: string;
  weight_per_batch_kg: string;
}

const emptyItem = (): FormItem => ({
  raw_material_id: "",
  percentage: "",
  weight_per_batch_kg: "",
});

export default function FeedFormulasPage() {
  const isOwner = useIsOwner();
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState<FeedFormula | null>(null);
  const [editing, setEditing] = useState<FeedFormula | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<FeedFormula | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [targetStage, setTargetStage] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);
  const [items, setItems] = useState<FormItem[]>([emptyItem()]);

  const loadData = async () => {
    try {
      const [formulasData, rmData] = await Promise.all([
        feedFormulaService.list(),
        rawMaterialService.list(),
      ]);
      setFormulas(formulasData);
      setRawMaterials(rmData);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const viewDetail = async (formula: FeedFormula) => {
    try {
      const data = await feedFormulaService.get(formula.id);
      setSelectedFormula(data);
      setDetailOpen(true);
    } catch {
      // silently fail
    }
  };

  const confirmDelete = (formula: FeedFormula) => {
    setDeleteTarget(formula);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await feedFormulaService.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName("");
    setTargetStage("");
    setNotes("");
    setActive(true);
    setItems([emptyItem()]);
    setError("");
    setInfo("");
    setModalOpen(true);
  };

  const openEdit = async (formula: FeedFormula) => {
    try {
      const data = await feedFormulaService.get(formula.id);
      setEditing(data);
      setName(data.name);
      setTargetStage(data.target_stage ?? "");
      setNotes(data.notes ?? "");
      setActive(data.active);
      setItems(
        data.items && data.items.length > 0
          ? data.items.map((it: FeedFormulaItem) => ({
              raw_material_id: it.raw_material_id.toString(),
              percentage: it.percentage.toString(),
              weight_per_batch_kg: it.weight_per_batch_kg.toString(),
            }))
          : [emptyItem()]
      );
      setError("");
      setModalOpen(true);
    } catch {
      // silently fail
    }
  };

  const updateItem = (index: number, field: keyof FormItem, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        // Kgs/Ton is primary — auto-derive percentage from it
        if (field === "weight_per_batch_kg") {
          const kgsPerTon = parseFloat(value) || 0;
          updated.percentage = (kgsPerTon / 10).toString();
        }
        return updated;
      })
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index: number) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const validItems = items.filter((it) => it.raw_material_id && (it.percentage || it.weight_per_batch_kg));
    const payload = {
      name,
      target_stage: targetStage || null,
      notes: notes || null,
      active,
      items: validItems.map((it) => ({
        raw_material_id: parseInt(it.raw_material_id),
        percentage: parseFloat(it.percentage) || 0,
        weight_per_batch_kg: parseFloat(it.weight_per_batch_kg) || 0,
      })),
    };

    try {
      if (editing) {
        const result = await feedFormulaService.update(editing.id, payload);
        if (result?.copied) {
          setInfo(`Formula was in use — saved as a new formula "${name}". The original remains unchanged.`);
        }
      } else {
        await feedFormulaService.create(payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const getRawMaterialName = (id: number) =>
    rawMaterials.find((rm) => rm.id === id)?.name ?? `#${id}`;

  return (
    <>
      <PageMeta
        title="Feed Formulas | Poultry Book"
        description="Manage feed formulas"
      />
      <PageBreadcrumb pageTitle="Feed Formulas" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Feed Formulas
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Formula
            </Button>
          )}
        </div>

        {info && (
          <div className="mx-5 mb-4 flex items-center justify-between rounded-lg bg-brand-50 p-3 text-sm text-brand-700 dark:bg-brand-500/15 dark:text-brand-400 sm:mx-6">
            <span>{info}</span>
            <button onClick={() => setInfo("")} className="ml-3 text-brand-500 hover:text-brand-700">&times;</button>
          </div>
        )}

        {error && !modalOpen && !detailOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : formulas.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No feed formulas found. Add your first formula.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Stage
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formulas.map((f) => (
                  <TableRow
                    key={f.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      <button
                        onClick={() => viewDetail(f)}
                        className="text-left hover:text-brand-500 dark:hover:text-brand-400"
                      >
                        {f.name}
                      </button>
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {f.target_stage ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge
                        size="sm"
                        color={f.active ? "success" : "light"}
                      >
                        {f.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-5 py-3 text-right">
                      <button
                        onClick={() => viewDetail(f)}
                        className="mr-3 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        View
                      </button>
                      {isOwner && (
                        <>
                          <button
                            onClick={() => openEdit(f)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => confirmDelete(f)}
                            className="ml-3 text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        className="max-w-lg p-5 sm:p-8 mx-4"
      >
        {selectedFormula && (
          <>
            <div className="mb-4 pr-10">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {selectedFormula.name}
              </h4>
              <div className="mt-1">
                <Badge
                  size="sm"
                  color={selectedFormula.active ? "success" : "light"}
                >
                  {selectedFormula.active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>

            {selectedFormula.target_stage && (
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                Stage:{" "}
                <span className="capitalize text-gray-700 dark:text-gray-300">
                  {selectedFormula.target_stage}
                </span>
              </p>
            )}
            {selectedFormula.notes && (
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                {selectedFormula.notes}
              </p>
            )}

            {selectedFormula.items && selectedFormula.items.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell isHeader className="px-4 py-2.5 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        Material
                      </TableCell>
                      <TableCell isHeader className="px-4 py-2.5 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        Kgs/Ton
                      </TableCell>
                      <TableCell isHeader className="px-4 py-2.5 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        %
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedFormula.items.map((item, idx) => (
                      <TableRow
                        key={idx}
                        className="border-t border-gray-100 dark:border-gray-800"
                      >
                        <TableCell className="px-4 py-2.5 text-sm text-gray-800 dark:text-white/90">
                          {item.raw_material_name ?? getRawMaterialName(item.raw_material_id)}
                        </TableCell>
                        <TableCell className="px-4 py-2.5 text-sm text-right text-gray-600 dark:text-gray-400">
                          {item.weight_per_batch_kg}
                        </TableCell>
                        <TableCell className="px-4 py-2.5 text-sm text-right text-gray-600 dark:text-gray-400">
                          {item.percentage}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No ingredients defined.</p>
            )}
          </>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-xl p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Feed Formula" : "Add Feed Formula"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="ffName">Name *</Label>
            <Input
              id="ffName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Layer Formula A"
              required
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="targetStage">Target Stage</Label>
              <select
                id="targetStage"
                value={targetStage}
                onChange={(e) => setTargetStage(e.target.value)}
                className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                <option value="">None</option>
                <option value="brooder">Brooder</option>
                <option value="grower">Grower</option>
                <option value="layer">Layer</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                Active
              </label>
            </div>
          </div>

          <div>
            <Label htmlFor="ffNotes">Notes</Label>
            <textarea
              id="ffNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          {/* Formula Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Ingredients</Label>
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
              >
                + Add Row
              </button>
            </div>

            <div className="flex items-center gap-2 mb-1 text-xs text-gray-400 dark:text-gray-500">
              <span className="flex-1">Material</span>
              <span className="w-24 text-center">Kgs/Ton</span>
              <span className="w-12"></span>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={item.raw_material_id}
                    onChange={(e) =>
                      updateItem(idx, "raw_material_id", e.target.value)
                    }
                    className="h-10 flex-1 rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  >
                    <option value="">Material</option>
                    {rawMaterials.map((rm) => (
                      <option key={rm.id} value={rm.id}>
                        {rm.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    value={item.weight_per_batch_kg}
                    onChange={(e) =>
                      updateItem(idx, "weight_per_batch_kg", e.target.value)
                    }
                    placeholder="Kgs/Ton"
                    min={0}
                    step="any"
                    className="!h-10 w-24"
                  />
                  <span className="text-xs text-gray-400 w-12 text-right shrink-0">
                    {item.weight_per_batch_kg ? `${(parseFloat(item.weight_per_batch_kg) / 10).toFixed(1)}%` : ""}
                  </span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="flex-shrink-0 text-error-500 hover:text-error-600 text-lg leading-none"
                      title="Remove"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Formula"
        message={
          <>
            Are you sure you want to delete{" "}
            <span className="font-medium text-gray-800 dark:text-white/90">
              {deleteTarget?.name}
            </span>
            ? This cannot be undone.
            {error && deleteTarget && (
              <div className="mt-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}
